<template>
    <b-modal
        ref="assignmentModal"
        :visible="visible"
        title="Назначения сотрудника"
        size="lg"
        centered
        scrollable

        modal-class="assignment-manager-modal workforce-viewport-modal"
        :no-close-on-backdrop="busy"
        :no-close-on-esc="busy"
        :hide-header-close="busy"
        @show="onShow"
        @shown="refreshModalLayout"
        @hidden="onHidden"
    >
        <b-alert
            v-if='externalChangePending'
            show
            variant='warning'
            class='assignment-manager__external-change'
        >
            <i class='fas fa-sync-alt mr-1' aria-hidden='true'></i>
            Данные доски изменились у другого пользователя. Введённые значения
            сохранены; при отправке Аргус проверит их по актуальному назначению.
        </b-alert>
        <div class="assignment-manager">
            <div
                v-if="!employee"
                class="assignment-manager__employee-picker"
            >
                <div
                    class="assignment-manager__employee-filter"
                    role="group"
                    aria-label="Список сотрудников"
                >
                    <b-button
                        type="button"
                        size="sm"
                        :variant="
                            employeeListMode === 'available'
                                ? 'primary'
                                : 'outline-secondary'
                        "
                        :aria-pressed="employeeListMode === 'available'"
                        @click="setEmployeeListMode('available')"
                    >
                        Дома или требуют распределения
                        <span class="assignment-manager__filter-count">
                            {{ availableEmployeeCount }}
                        </span>
                    </b-button>
                    <b-button
                        type="button"
                        size="sm"
                        :variant="
                            employeeListMode === 'all'
                                ? 'primary'
                                : 'outline-secondary'
                        "
                        :aria-pressed="employeeListMode === 'all'"
                        @click="setEmployeeListMode('all')"
                    >
                        Все
                        <span class="assignment-manager__filter-count">
                            {{ allEmployeeCount }}
                        </span>
                    </b-button>
                </div>
                <b-form-group
                    label="Сотрудник"
                    label-for="assignment-manager-employee"
                    class="assignment-manager__employee-select"
                >
                    <v-select
                        id="assignment-manager-employee"
                        v-model="selectedEmployeeId"
                        :options="employeeSelectOptions"
                        :reduce="option => option.value"
                        label="text"
                        :clearable="false"
                        placeholder="Начните вводить фамилию"
                    >
                        <template #option="option">
                            <div class="assignment-manager__employee-option">
                                <strong>{{ option.text }}</strong>
                                <small v-if="option.position">
                                    {{ option.position }}
                                </small>
                            </div>
                        </template>
                        <template #no-options>
                            Нет сотрудников в выбранном списке
                        </template>
                    </v-select>
                </b-form-group>
            </div>

            <template v-if="selectedEmployee">
                <header class="assignment-manager__employee">
                    <div class="assignment-manager__employee-details">
                        <span>Сотрудник</span>
                        <strong>{{ employeeName(selectedEmployee) }}</strong>
                        <small v-if="selectedEmployee.position">
                            {{ selectedEmployee.position }}
                        </small>
                    </div>
                    <div class="assignment-manager__employee-actions">
                        <b-button
                            v-if="showEmployeeCardAction"
                            size="sm"
                            variant="outline-secondary"
                            :disabled="busy"
                            @click="$emit('open-card', selectedEmployee)"
                        >
                            <i class="fas fa-address-card mr-1" aria-hidden="true"></i>
                            Карточка сотрудника
                        </b-button>
                        <b-button
                            v-if="!showAddForm && canOfferAddAssignment"
                            size="sm"
                            variant="outline-primary"
                            :disabled="
                                busy ||
                                additionalAssignmentChangesBlocked
                            "
                            :title="
                                additionalAssignmentChangesBlocked
                                    ? futureAdditionalAssignmentsHint
                                    : null
                            "
                            @click="openAddForm"
                        >
                            <i class="fas fa-plus mr-1" aria-hidden="true"></i>
                            {{ addActionLabel }}
                        </b-button>
                    </div>
                </header>

                <b-alert
                    v-if="selectedEmployee.has_hidden_assignments"
                    show
                    variant="info"
                    class="assignment-manager__hidden-alert"
                >
                    <i class="fas fa-lock mr-1" aria-hidden="true"></i>
                    У сотрудника есть назначения на недоступных вам объектах.
                    Их названия и количество не раскрываются.
                </b-alert>

                <b-alert
                    v-if="futureDate"
                    show
                    variant="info"
                    class="assignment-manager__hidden-alert"
                >
                    <i class="fas fa-calendar-alt mr-1" aria-hidden="true"></i>
                    {{ futureAdditionalAssignmentsHint }}
                </b-alert>

                <section class="assignment-manager__section">
                    <h3>Назначения на {{ formattedBoardDate }}</h3>
                    <div
                        v-if="visibleAssignments.length"
                        class="assignment-manager__assignments"
                    >
                        <article
                            v-for="assignment in visibleAssignments"
                            :key="assignmentKey(assignment)"
                            class="assignment-manager__assignment"
                        >
                            <div class="assignment-manager__assignment-main">
                                <div class="assignment-manager__assignment-title">
                                    <i
                                        :class="assignmentIcon(assignment)"
                                        aria-hidden="true"
                                    ></i>
                                    <strong>{{ assignmentName(assignment) }}</strong>
                                </div>
                                <div class="assignment-manager__assignment-meta">
                                    <b-badge
                                        :variant="
                                            assignmentIsPrimary(assignment)
                                                ? 'primary'
                                                : 'info'
                                        "
                                    >
                                        {{
                                            assignmentIsPrimary(assignment)
                                                ? 'Основное'
                                                : 'Дополнительное'
                                        }}
                                    </b-badge>
                                    <span v-if="assignment.role_label">
                                        {{ assignment.role_label }}
                                    </span>
                                </div>
                            </div>
                            <div class="assignment-manager__assignment-actions">
                                <b-button
                                    v-if="assignmentIsPrimary(assignment)"
                                    size="sm"
                                    variant="outline-primary"
                                    :disabled="busy || !canMoveAssignment(assignment)"
                                    @click="openMoveForm"
                                >
                                    Переместить на другой объект
                                </b-button>
                                <b-button
                                    v-else-if="canEndAssignment(assignment)"
                                    size="sm"
                                    variant="outline-danger"
                                    :disabled="busy || futureDate"
                                    :title="futureDate ? futureAdditionalAssignmentsHint : null"
                                    @click="endAssignment(assignment)"
                                >
                                    Снять
                                </b-button>
                            </div>
                        </article>
                    </div>
                    <div v-else class="assignment-manager__empty">
                        Видимых назначений на выбранную дату нет.
                    </div>
                </section>

                <section
                    v-if="showMoveForm"
                    class="assignment-manager__form assignment-manager__form--primary"
                >
                    <div class="assignment-manager__form-heading">
                        <div>
                            <strong>{{ moveFormTitle }}</strong>
                            <span>{{ moveFormHint }}</span>
                        </div>
                        <button
                            type="button"
                            aria-label="Закрыть форму перемещения"
                            :disabled="busy"
                            @click="closeForms"
                        >
                            <i class="fas fa-times" aria-hidden="true"></i>
                        </button>
                    </div>
                    <b-form-group
                        label="Куда назначить"
                        label-for="assignment-manager-primary-target"
                    >
                        <v-select
                            id="assignment-manager-primary-target"
                            v-model="moveTargetKey"
                            :options="primaryDestinationOptions"
                            :reduce="option => option.value"
                            :selectable="option => option.disabled !== true"
                            label="text"
                            :clearable="true"
                            :searchable="true"
                            append-to-body
                            :calculate-position="positionModalSelectDropdown"
                            placeholder="Выберите зону, группу или объект"
                            :disabled="busy"
                        >
                            <template #option="option">
                                <div class="assignment-manager__object-option">
                                    <span>{{ option.text }}</span>
                                    <small v-if="option.disabled">
                                        Уже назначен дополнительно
                                    </small>
                                </div>
                            </template>
                            <template #no-options>
                                Места назначения не найдены
                            </template>
                        </v-select>
                    </b-form-group>
                    <b-form-group
                        :label="moveDateLabel"
                        label-for="assignment-manager-primary-date"
                    >
                        <b-form-input
                            id="assignment-manager-primary-date"
                            v-model="moveEffectiveDate"
                            type="date"
                            :state="moveEffectiveDate ? moveDateValid : null"
                            :disabled="busy"
                        ></b-form-input>
                        <b-form-text>
                            {{ moveDateHint }}
                        </b-form-text>
                    </b-form-group>
                    <b-form-group
                        label="Примечание (необязательно)"
                        label-for="assignment-manager-primary-note"
                    >
                        <AppTextarea
                            id="assignment-manager-primary-note"
                            v-model.trim="moveNote"
                            :disabled="busy"
                        ></AppTextarea>
                    </b-form-group>
                    <div class="assignment-manager__form-actions">
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            :disabled="busy"
                            @click="closeForms"
                        >
                            Отмена
                        </b-button>
                        <b-button
                            size="sm"
                            variant="primary"
                            :disabled="busy || !moveTargetKey || !moveDateValid"
                            @click="movePrimary"
                        >
                            <b-spinner v-if="pending" small class="mr-1"></b-spinner>
                            {{ moveActionLabel }}
                        </b-button>
                    </div>
                </section>

                <section
                    v-if="showAddForm"
                    class="assignment-manager__form assignment-manager__form--additional"
                >
                    <div class="assignment-manager__form-heading">
                        <div>
                            <strong>{{ addFormTitle }}</strong>
                            <span>{{ addFormHint }}</span>
                        </div>
                        <button
                            type="button"
                            aria-label="Закрыть форму добавления"
                            :disabled="busy"
                            @click="closeForms"
                        >
                            <i class="fas fa-times" aria-hidden="true"></i>
                        </button>
                    </div>
                    <b-form-group
                        :label="initialTargetIsGroup ? 'Группа объектов' : 'Объект'"
                        label-for="assignment-manager-add-target"
                    >
                        <b-form-input
                            v-if="initialTargetIsGroup"
                            id="assignment-manager-add-target"
                            :value="initialTargetLabel"
                            readonly
                            :disabled="busy"
                        ></b-form-input>
                        <v-select
                            v-else
                            id="assignment-manager-add-target"
                            v-model="addBuildingId"
                            :options="additionalObjectOptions"
                            :reduce="option => option.value"
                            :selectable="option => option.disabled !== true"
                            label="text"
                            :clearable="true"
                            :searchable="true"
                            append-to-body
                            :calculate-position="positionModalSelectDropdown"
                            placeholder="Начните вводить название объекта"
                            :disabled="busy || additionalAssignmentChangesBlocked"
                        >
                            <template #option="option">
                                <div class="assignment-manager__object-option">
                                    <span>{{ option.text }}</span>
                                    <small v-if="option.disabled">
                                        Уже назначен
                                    </small>
                                </div>
                            </template>
                            <template #no-options>
                                Объекты не найдены
                            </template>
                        </v-select>
                    </b-form-group>
                    <b-form-group
                        label="Дата начала назначения"
                        label-for="assignment-manager-add-date"
                    >
                        <b-form-input
                            id="assignment-manager-add-date"
                            v-model="addEffectiveDate"
                            type="date"
                            :state="addEffectiveDate ? addDateValid : null"
                            :disabled="busy || additionalAssignmentChangesBlocked"
                        ></b-form-input>
                    </b-form-group>
                    <b-form-group
                        label="Примечание (необязательно)"
                        label-for="assignment-manager-add-note"
                    >
                        <AppTextarea
                            id="assignment-manager-add-note"
                            v-model.trim="addNote"
                            :disabled="busy || additionalAssignmentChangesBlocked"
                        ></AppTextarea>
                    </b-form-group>
                    <b-alert
                        v-if="addTargetAlreadyAssigned"
                        show
                        variant="info"
                        class="py-2"
                    >
                        Сотрудник уже назначен на выбранное место.
                    </b-alert>
                    <div class="assignment-manager__form-actions">
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            :disabled="busy"
                            @click="closeForms"
                        >
                            Отмена
                        </b-button>
                        <b-button
                            size="sm"
                            variant="primary"
                            :disabled="
                                busy ||
                                additionalAssignmentChangesBlocked ||
                                !addTargetReady ||
                                !addDateValid ||
                                addTargetAlreadyAssigned
                            "
                            @click="addObjectAssignment"
                        >
                            <b-spinner v-if="localPending" small class="mr-1"></b-spinner>
                            {{ addActionLabel }}
                        </b-button>
                    </div>
                </section>

                <b-alert v-if="errorMessage" show variant="danger" class="mb-0 mt-3">
                    {{ errorMessage }}
                </b-alert>
            </template>

            <div v-else class="assignment-manager__employee-empty">
                Выберите сотрудника, которого нужно назначить на площадку.
            </div>
        </div>
        <template #modal-footer><b-alert v-if="actionError" show variant="danger" role="alert" class="w-100 mb-0">{{ actionError }}</b-alert></template>
    </b-modal>
</template>

<script>
import api from '@/utils/api';
import { positionVueSelectDropdown } from '@/utils/vueSelectPositioning';

function positiveInteger(value) {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : null;
}

function localIsoDate() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function validIsoDate(value) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));
}

export default {
    name: 'EmployeeAssignmentManager',
    props: {
        actionError: {type: String, default: ''},
        visible: {
            type: Boolean,
            default: false
        },
        employee: {
            type: Object,
            default: null
        },
        employeeOptions: {
            type: Array,
            default: () => []
        },
        unassignedEmployeeOptions: {
            type: Array,
            default: () => []
        },
        destinationOptions: {
            type: Array,
            default: () => []
        },
        initialTarget: {
            type: Object,
            default: null
        },
        boardDate: {
            type: String,
            required: true
        },
        futureDate: {
            type: Boolean,
            default: false
        },
        initialForm: {
            type: String,
            default: ''
        },
        initialEffectiveDate: {
            type: String,
            default: ''
        },
        pending: {
            type: Boolean,
            default: false
        },
        externalChangePending: {
            type: Boolean,
            default: false
        },
        showEmployeeCardAction: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedEmployeeId: null,
            employeeListMode: 'available',
            activeForm: '',
            moveTargetKey: '',
            moveEffectiveDate: '',
            moveNote: '',
            addBuildingId: null,
            addEffectiveDate: '',
            addNote: '',
            localPending: false,
            errorMessage: ''
        };
    },
    computed: {
        busy() {
            return this.pending || this.localPending;
        },
        availableEmployeeCount() {
            return this.normalizedEmployeeOptions(
                this.unassignedEmployeeOptions
            ).length;
        },
        allEmployeeCount() {
            return this.normalizedEmployeeOptions(this.employeeOptions).length;
        },
        employeeSelectOptions() {
            const source = this.employeeListMode === 'all'
                ? this.employeeOptions
                : this.unassignedEmployeeOptions;
            return this.normalizedEmployeeOptions(source)
                .map(employee => ({
                    value: this.employeeId(employee),
                    text: this.employeeName(employee),
                    position: employee.position || '',
                    employee
                }))
                .filter(option => option.value);
        },
        initialTargetIsGroup() {
            return Boolean(
                this.initialTarget && this.initialTarget.type === 'object_group'
            );
        },
        initialTargetIsPlacement() {
            return Boolean(
                this.initialTarget &&
                (
                    this.initialTarget.type === 'object' ||
                    this.initialTarget.type === 'object_group'
                )
            );
        },
        initialTargetKey() {
            if (!this.initialTargetIsPlacement) return '';
            return `${this.initialTarget.type}:${this.initialTarget.id}`;
        },
        initialTargetLabel() {
            const option = this.destinationOptions.find(item => (
                String(item && item.value || '') === this.initialTargetKey
            ));
            return option
                ? option.text
                : `Группа объектов #${this.initialTarget && this.initialTarget.id || ''}`;
        },
        assignSelectedAsPrimary() {
            const projectionNeedsPrimary = Boolean(
                this.employee &&
                this.employee.workforce_timesheet_projection === true &&
                this.employee.multi_assignment_eligible !== true
            );
            return projectionNeedsPrimary || (
                !this.employee &&
                this.employeeListMode === 'available' &&
                this.initialTargetIsPlacement
            );
        },
        selectedEmployee() {
            if (this.employee) {
                return this.employee;
            }
            const option = this.employeeSelectOptions.find(item => (
                String(item.value) === String(this.selectedEmployeeId)
            ));
            return option ? option.employee : null;
        },
        visibleAssignments() {
            const employee = this.selectedEmployee;
            if (!employee) {
                return [];
            }
            if (Array.isArray(employee.visible_assignments)) {
                return employee.visible_assignments;
            }
            const assignment = employee.assignment ||
                employee.assignment_context ||
                null;
            return assignment ? [assignment] : [];
        },
        formattedBoardDate() {
            const parts = String(this.boardDate || '').split('-');
            return parts.length === 3
                ? `${parts[2]}.${parts[1]}.${parts[0]}`
                : String(this.boardDate || '');
        },
        showMoveForm() {
            return this.activeForm === 'move';
        },
        showAddForm() {
            return this.activeForm === 'add' && this.canOfferAddAssignment;
        },
        primaryDestinationOptions() {
            return this.destinationOptions.filter(option => (
                Boolean(option && option.value)
            )).map(option => {
                const value = String(option && option.value || '');
                if (!value.startsWith('object:')) {
                    return option;
                }
                const buildingId = positiveInteger(value.slice('object:'.length));
                if (
                    !buildingId ||
                    !this.hasAdditionalObjectAssignment(buildingId)
                ) {
                    return option;
                }
                return Object.assign({}, option, {
                    disabled: true,
                    text: `${option.text} — уже дополнительный`
                });
            });
        },
        moveDateValid() {
            return validIsoDate(this.moveEffectiveDate);
        },
        addDateValid() {
            return validIsoDate(this.addEffectiveDate);
        },
        moveDateFuture() {
            return this.moveDateValid && this.moveEffectiveDate > localIsoDate();
        },
        addDateFuture() {
            return this.addDateValid && this.addEffectiveDate > localIsoDate();
        },
        moveFormTitle() {
            return this.needsPrimaryAssignment
                ? 'Назначить основное место'
                : 'Переместить на другое место';
        },
        moveFormHint() {
            return this.needsPrimaryAssignment
                ? 'Можно выбрать общую зону, группу или объект.'
                : 'Дополнительные назначения сохранятся.';
        },
        moveDateLabel() {
            return this.needsPrimaryAssignment
                ? 'Дата начала назначения'
                : 'Дата переноса';
        },
        moveDateHint() {
            return this.moveDateFuture
                ? 'Назначение будет сохранено как плановое.'
                : 'При будущей дате назначение будет сохранено как плановое.';
        },
        moveActionLabel() {
            if (this.needsPrimaryAssignment) {
                return this.moveDateFuture
                    ? 'Запланировать назначение'
                    : 'Назначить';
            }
            return this.moveDateFuture
                ? 'Запланировать перемещение на другой объект'
                : 'Переместить на другой объект';
        },
        additionalObjectOptions() {
            const options = [];
            this.destinationOptions.forEach(option => {
                const value = String(option && option.value || '');
                if (!value.startsWith('object:')) {
                    return;
                }
                const buildingId = positiveInteger(value.slice('object:'.length));
                if (!buildingId) {
                    return;
                }
                options.push({
                    value: buildingId,
                    text: option.text,
                    disabled: this.hasVisibleObjectAssignment(buildingId)
                });
            });
            return options;
        },
        canAddDestination() {
            if (this.initialTargetIsGroup) return true;
            if (this.needsPrimaryAssignment) {
                return this.primaryDestinationOptions.some(option => (
                    option && option.value && option.disabled !== true
                ));
            }
            return this.additionalObjectOptions.some(option => (
                option.value && option.disabled !== true
            ));
        },
        canOfferAddAssignment() {
            if (!this.selectedEmployee || !this.canAddDestination) {
                return false;
            }
            return this.initialTargetIsGroup ||
                this.needsPrimaryAssignment ||
                this.selectedEmployee.multi_assignment_eligible === true;
        },
        addTargetReady() {
            return this.initialTargetIsGroup
                ? Boolean(positiveInteger(this.initialTarget && this.initialTarget.id))
                : Boolean(this.addBuildingId);
        },
        addTargetAlreadyAssigned() {
            if (this.initialTargetIsGroup) {
                return this.visibleAssignments.some(assignment => (
                    this.assignmentTargetType(assignment) === 'object_group' &&
                    String(this.assignmentTargetId(assignment)) ===
                        String(this.initialTarget.id)
                ));
            }
            return Boolean(
                this.addBuildingId &&
                this.hasVisibleObjectAssignment(this.addBuildingId)
            );
        },
        needsPrimaryAssignment() {
            if (!this.selectedEmployee) {
                return false;
            }
            if (this.assignSelectedAsPrimary) return true;
            if (this.selectedEmployee.has_hidden_assignments) return false;
            const fallbackAssignment = this.selectedEmployee.assignment ||
                this.selectedEmployee.assignment_context ||
                null;
            const assignments = this.visibleAssignments.slice();
            if (fallbackAssignment) {
                assignments.push(fallbackAssignment);
            }
            if (assignments.some(assignment => (
                this.assignmentKind(assignment) === 'additional'
            ))) {
                return false;
            }
            const primaryAssignment = assignments.find(assignment => (
                this.assignmentIsPrimary(assignment)
            ));
            const primaryTargetType = this.assignmentTargetType(primaryAssignment);
            return !primaryAssignment ||
                primaryTargetType === 'home' ||
                primaryTargetType === 'requires_distribution';
        },
        additionalAssignmentChangesBlocked() {
            return false;
        },
        addFormTitle() {
            if (this.initialTargetIsGroup) {
                return 'Назначить на группу';
            }
            return this.needsPrimaryAssignment
                ? 'Назначить основное место'
                : 'Добавить дополнительный объект';
        },
        addFormHint() {
            if (this.initialTargetIsGroup) {
                return this.addDateFuture
                    ? `Будет создано плановое перемещение основного места на ${
                        this.formatDate(this.addEffectiveDate)
                    }.`
                    : 'Выбранная группа станет основным местом сотрудника.';
            }
            if (!this.needsPrimaryAssignment) {
                return this.addDateFuture
                    ? 'Основное место сотрудника не изменится. ' +
                        'Дополнительное назначение начнётся с указанной даты.'
                    : 'Основное место сотрудника не изменится.';
            }
            return this.addDateFuture
                ? `Будет создано плановое перемещение основного места на ${
                    this.formatDate(this.addEffectiveDate)
                }.`
                : 'Выбранный объект станет основным местом сотрудника.';
        },
        addActionLabel() {
            if (this.initialTargetIsGroup) {
                return this.addDateFuture
                    ? 'Запланировать назначение на группу'
                    : 'Назначить на группу';
            }
            if (this.needsPrimaryAssignment) {
                return 'Назначить';
            }
            return 'Добавить дополнительный объект';
        },
        futureAdditionalAssignmentsHint() {
            return 'Дату начала нового назначения можно указать в форме. ' +
                'Снять дополнительное назначение будущей датой пока нельзя.';
        },
        formDirty() {
            if (!this.visible) return false;
            if (!this.employee && this.selectedEmployeeId) return true;
            if (this.activeForm === 'move') {
                return Boolean(
                    this.moveTargetKey ||
                    this.moveNote ||
                    this.moveEffectiveDate !== this.defaultEffectiveDate()
                );
            }
            if (this.activeForm === 'add') {
                const initialBuildingId = this.initialTarget &&
                    this.initialTarget.type === 'object'
                    ? positiveInteger(this.initialTarget.id)
                    : null;
                return Boolean(
                    this.addNote ||
                    this.addEffectiveDate !== this.defaultEffectiveDate() ||
                    String(this.addBuildingId || '') !==
                        String(initialBuildingId || '')
                );
            }
            return false;
        }
    },
    watch: {
        visible(value) {
            if (value) {
                this.reset();
            }
        },
        employee(employee, previousEmployee) {
            if (
                this.visible &&
                this.employeeId(employee) !== this.employeeId(previousEmployee)
            ) {
                this.reset();
            }
        },
        selectedEmployeeId(value) {
            this.addNote = '';
            this.addEffectiveDate = this.defaultEffectiveDate();
            this.moveNote = '';
            this.moveEffectiveDate = this.defaultEffectiveDate();
            this.errorMessage = '';
            if (value && this.initialTargetIsPlacement) {
                this.activeForm = 'add';
                this.addBuildingId = this.initialTarget.type === 'object'
                    ? positiveInteger(this.initialTarget.id)
                    : null;
            }
            if (this.visible) {
                this.refreshModalLayout();
            }
        },
        activeForm() {
            if (this.visible) {
                this.refreshModalLayout();
            }
        },
        formDirty(value) {
            this.$emit('editing-state', value === true);
        }
    },
    beforeDestroy() {
        this.$emit('editing-state', false);
        this.removeModalLayerClass();
    },
    methods: {
        positionModalSelectDropdown: positionVueSelectDropdown,
        onShow() {
            if (typeof document !== 'undefined' && document.body) {
                document.body.classList.add('assignment-manager-modal-open');
            }
        },
        refreshModalLayout() {
            if (typeof window === 'undefined') {
                return;
            }
            this.$nextTick(() => {
                const schedule = typeof window.requestAnimationFrame === 'function'
                    ? window.requestAnimationFrame.bind(window)
                    : callback => window.setTimeout(callback, 0);
                schedule(() => {
                    schedule(() => {
                        if (!this.visible || this._isDestroyed) {
                            return;
                        }
                        const modal = this.$refs.assignmentModal;
                        if (modal && typeof modal.checkModalOverflow === 'function') {
                            modal.checkModalOverflow();
                        }
                        window.dispatchEvent(new window.Event('resize'));
                    });
                });
            });
        },
        onHidden() {
            this.removeModalLayerClass();
            this.$emit('editing-state', false);
            this.$emit('close');
        },
        removeModalLayerClass() {
            if (typeof document !== 'undefined' && document.body) {
                document.body.classList.remove('assignment-manager-modal-open');
            }
        },
        reset() {
            this.employeeListMode = 'available';
            this.selectedEmployeeId = this.employeeId(this.employee);
            this.activeForm = this.initialTargetIsPlacement
                ? 'add'
                : (this.initialForm === 'move' ? 'move' : '');
            this.moveTargetKey = '';
            this.moveEffectiveDate = this.defaultEffectiveDate();
            this.moveNote = '';
            this.addBuildingId = this.initialTarget && this.initialTarget.type === 'object'
                ? positiveInteger(this.initialTarget.id)
                : null;
            this.addEffectiveDate = this.defaultEffectiveDate();
            this.addNote = '';
            this.localPending = false;
            this.errorMessage = '';
        },
        setEmployeeListMode(mode) {
            if (mode !== 'available' && mode !== 'all') {
                return;
            }
            this.employeeListMode = mode;
            const selectedStillVisible = this.employeeSelectOptions.some(option => (
                String(option.value) === String(this.selectedEmployeeId)
            ));
            if (this.selectedEmployeeId && !selectedStillVisible) {
                this.selectedEmployeeId = null;
                this.activeForm = '';
                this.addBuildingId = null;
                this.addNote = '';
                this.errorMessage = '';
            }
        },
        normalizedEmployeeOptions(source) {
            const seen = {};
            return (Array.isArray(source) ? source : []).filter(employee => {
                const id = this.employeeId(employee);
                if (
                    !employee ||
                    !id ||
                    employee.lifecycle_status === 'reserve' ||
                    seen[id]
                ) {
                    return false;
                }
                seen[id] = true;
                return true;
            });
        },
        employeePrimaryAssignment(employee) {
            if (!employee) {
                return null;
            }
            if (employee.primary_assignment) {
                return employee.primary_assignment;
            }
            const visibleAssignments = Array.isArray(employee.visible_assignments)
                ? employee.visible_assignments
                : [];
            const visiblePrimary = visibleAssignments.find(assignment => (
                this.assignmentIsPrimary(assignment)
            ));
            if (visiblePrimary) {
                return visiblePrimary;
            }
            const directAssignment = employee.assignment || null;
            const directAssignmentKind = directAssignment
                ? String(
                    directAssignment.kind ||
                    directAssignment.assignment_kind ||
                    ''
                )
                : '';
            if (
                directAssignment &&
                directAssignmentKind !== 'additional' &&
                directAssignment.is_primary !== false
            ) {
                return directAssignment;
            }
            const assignmentContext = employee.assignment_context || null;
            return assignmentContext &&
                this.assignmentIsPrimary(assignmentContext)
                ? assignmentContext
                : null;
        },
        employeeIsAvailableForInitialAssignment(employee) {
            const primaryAssignment = this.employeePrimaryAssignment(employee);
            if (!primaryAssignment) {
                return true;
            }
            const targetType = this.assignmentTargetType(primaryAssignment);
            return targetType === 'home' ||
                targetType === 'requires_distribution';
        },
        employeeId(employee) {
            return employee
                ? positiveInteger(employee.employee_id || employee.id)
                : null;
        },
        employeeName(employee) {
            if (!employee) {
                return '';
            }
            return employee.display_name ||
                employee.full_name ||
                employee.name ||
                `Сотрудник #${this.employeeId(employee) || ''}`;
        },
        assignmentId(assignment) {
            return positiveInteger(
                assignment && (assignment.assignment_id || assignment.id)
            );
        },
        assignmentKind(assignment) {
            if (!assignment) {
                return '';
            }
            if (assignment.kind) {
                return String(assignment.kind);
            }
            if (assignment.assignment_kind) {
                return String(assignment.assignment_kind);
            }
            return assignment.is_primary === true ? 'primary' : 'additional';
        },
        assignmentIsPrimary(assignment) {
            return this.assignmentKind(assignment) === 'primary';
        },
        assignmentTargetType(assignment) {
            return String(
                assignment && (assignment.target_type || assignment.type) || ''
            );
        },
        assignmentTargetId(assignment) {
            if (!assignment) {
                return null;
            }
            return positiveInteger(
                assignment.target_id ||
                assignment.building_id ||
                assignment.object_group_id
            );
        },
        assignmentKey(assignment) {
            const kind = this.assignmentKind(assignment) || 'unknown';
            const assignmentId = this.assignmentId(assignment);
            return [
                kind,
                assignmentId || [
                    this.assignmentTargetType(assignment) || 'none',
                    this.assignmentTargetId(assignment) || 'common'
                ].join(':')
            ].join(':');
        },
        assignmentName(assignment) {
            if (!assignment) {
                return 'Место не указано';
            }
            if (assignment.target_name || assignment.name) {
                return assignment.target_name || assignment.name;
            }
            const type = this.assignmentTargetType(assignment);
            if (type === 'home') {
                return 'Домой';
            }
            if (type === 'requires_distribution') {
                return 'Требует распределения';
            }
            if (type === 'object_group') {
                return `Группа объектов #${this.assignmentTargetId(assignment) || ''}`;
            }
            if (type === 'object') {
                return `Объект #${this.assignmentTargetId(assignment) || ''}`;
            }
            return 'Место не указано';
        },
        assignmentIcon(assignment) {
            const type = this.assignmentTargetType(assignment);
            if (type === 'home') {
                return 'fas fa-home';
            }
            if (type === 'object_group') {
                return 'fas fa-layer-group';
            }
            if (type === 'requires_distribution') {
                return 'fas fa-exclamation-circle';
            }
            return 'fas fa-map-marker-alt';
        },
        canMoveAssignment(assignment) {
            return assignment && assignment.can_move !== false &&
                assignment.can_edit !== false;
        },
        canEndAssignment(assignment) {
            return Boolean(
                this.assignmentId(assignment) &&
                assignment &&
                assignment.can_end !== false &&
                assignment.can_remove !== false &&
                assignment.can_edit !== false
            );
        },
        hasVisibleObjectAssignment(buildingId) {
            return this.visibleAssignments.some(assignment => (
                this.assignmentTargetType(assignment) === 'object' &&
                String(this.assignmentTargetId(assignment)) === String(buildingId)
            ));
        },
        hasAdditionalObjectAssignment(buildingId) {
            return this.visibleAssignments.some(assignment => (
                this.assignmentKind(assignment) === 'additional' &&
                this.assignmentTargetType(assignment) === 'object' &&
                String(this.assignmentTargetId(assignment)) === String(buildingId)
            ));
        },
        openMoveForm() {
            this.activeForm = 'move';
            this.moveTargetKey = '';
            this.moveEffectiveDate = this.defaultEffectiveDate();
            this.moveNote = '';
            this.errorMessage = '';
        },
        openAddForm() {
            if (this.needsPrimaryAssignment && !this.initialTargetIsGroup) {
                this.openMoveForm();
                return;
            }
            this.activeForm = 'add';
            this.addBuildingId = this.initialTarget &&
                this.initialTarget.type === 'object'
                ? positiveInteger(this.initialTarget.id)
                : null;
            this.addEffectiveDate = this.defaultEffectiveDate();
            this.addNote = '';
            this.errorMessage = '';
        },
        closeForms() {
            if (!this.busy) {
                this.activeForm = '';
                this.errorMessage = '';
            }
        },
        parseTargetKey(value) {
            const parts = String(value || '').split(':');
            if (!parts[0]) {
                return null;
            }
            return {
                type: parts[0],
                id: parts.slice(1).join(':') || null
            };
        },
        defaultEffectiveDate() {
            if (validIsoDate(this.initialEffectiveDate)) {
                return this.initialEffectiveDate;
            }
            return validIsoDate(this.boardDate)
                ? this.boardDate
                : localIsoDate();
        },
        formatDate(value) {
            const parts = String(value || '').split('-');
            return parts.length === 3
                ? `${parts[2]}.${parts[1]}.${parts[0]}`
                : String(value || '');
        },
        movePrimary() {
            const target = this.parseTargetKey(this.moveTargetKey);
            if (
                !target ||
                !this.selectedEmployee ||
                !this.moveDateValid ||
                this.busy
            ) {
                return;
            }
            this.errorMessage = '';
            this.$emit('move-primary', {
                employee: this.selectedEmployee,
                target,
                effectiveDate: this.moveEffectiveDate,
                note: this.moveNote
            });
        },
        async addObjectAssignment() {
            const employeeId = this.employeeId(this.selectedEmployee);
            const buildingId = positiveInteger(this.addBuildingId);
            const groupId = this.initialTargetIsGroup
                ? positiveInteger(this.initialTarget && this.initialTarget.id)
                : null;
            if (
                !employeeId ||
                (!buildingId && !groupId) ||
                !this.addDateValid ||
                this.busy ||
                this.addTargetAlreadyAssigned
            ) {
                return;
            }
            if (this.additionalAssignmentChangesBlocked) {
                this.errorMessage = this.futureAdditionalAssignmentsHint;
                return;
            }
            if (groupId || this.needsPrimaryAssignment) {
                this.errorMessage = '';
                this.$emit('move-primary', {
                    employee: this.selectedEmployee,
                    target: {
                        type: groupId ? 'object_group' : 'object',
                        id: groupId || buildingId
                    },
                    effectiveDate: this.addEffectiveDate,
                    note: this.addNote
                });
                return;
            }
            this.localPending = true;
            this.errorMessage = '';
            try {
                await api.post(
                    `/api/employees/${employeeId}/object-assignments`,
                    {
                        building_id: buildingId,
                        effective_date: this.addEffectiveDate,
                        note: this.addNote || null,
                        expected_state_token:
                            this.selectedEmployee.workforce_state_token || null,
                        expected_state_date:
                            this.selectedEmployee.workforce_state_date ||
                            this.boardDate
                    },
                    {ignoreModal: true}
                );
                const successMessage = this.addDateFuture
                    ? `Назначение сохранено с ${this.formatDate(this.addEffectiveDate)}`
                    : 'Объект добавлен в назначения сотрудника';
                this.$bvToast.toast(successMessage, {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
                this.$emit('changed', {
                    type: 'added',
                    employee_id: employeeId
                });
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось добавить назначение'
                );
            } finally {
                this.localPending = false;
            }
        },
        async endAssignment(assignment) {
            const assignmentId = this.assignmentId(assignment);
            if (!assignmentId || this.busy) {
                return;
            }
            if (this.futureDate) {
                this.errorMessage = this.futureAdditionalAssignmentsHint;
                return;
            }
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Снять сотрудника с объекта «${this.assignmentName(assignment)}»?`,
                {
                    title: 'Снять дополнительное назначение',
                    okTitle: 'Снять',
                    cancelTitle: 'Оставить',
                    okVariant: 'danger',
                    cancelVariant: 'outline-secondary',
                    centered: true
                }
            );
            if (!confirmed) {
                return;
            }
            this.localPending = true;
            this.errorMessage = '';
            try {
                await api.post(
                    `/api/employees/object-assignments/${assignmentId}/end`,
                    {
                        effective_date: this.boardDate,
                        reason: 'Снято с доски распределения'
                    },
                    {ignoreModal: true}
                );
                this.$bvToast.toast('Дополнительное назначение снято', {
                    variant: 'success',
                    solid: true,
                    autoHideDelay: 3000
                });
                this.$emit('changed', {
                    type: 'ended',
                    employee_id: this.employeeId(this.selectedEmployee),
                    assignment_id: assignmentId
                });
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось снять назначение'
                );
            } finally {
                this.localPending = false;
            }
        },
        errorText(error, fallback) {
            const data = error && error.response ? error.response.data : null;
            if (data && typeof data === 'object') {
                return data.msg || data.message || fallback;
            }
            if (typeof data === 'string' && data) {
                return data;
            }
            return fallback;
        }
    }
};
</script>

<style scoped>
.assignment-manager {
    color: #2d3e50;
}

.assignment-manager__employee-picker {
    position: sticky;
    z-index: 5;
    top: 0;
    margin: -1rem -1rem 16px;
    padding: 12px 1rem 10px;
    border-bottom: 1px solid #e3e8ee;
    background: #fff;
    box-shadow: 0 5px 10px rgba(31, 52, 73, 0.05);
}

.assignment-manager__employee-filter {
    display: flex;
    align-items: stretch;
    gap: 7px;
    margin-bottom: 10px;
}

.assignment-manager__employee-filter .btn {
    white-space: normal;
}

.assignment-manager__filter-count {
    display: inline-flex;
    min-width: 22px;
    justify-content: center;
    margin-left: 5px;
    padding: 1px 5px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.2);
    font-size: 0.7rem;
    font-weight: 700;
}

.assignment-manager__employee-filter .btn-outline-secondary
    .assignment-manager__filter-count {
    background: #edf1f5;
}

.assignment-manager__employee-select {
    margin-bottom: 0;
}

.assignment-manager__employee-option {
    display: flex;
    flex-direction: column;
}

.assignment-manager__employee-option small {
    color: #7c8895;
}

.assignment-manager__object-option {
    display: flex;
    flex-direction: column;
}

.assignment-manager__object-option small {
    color: #7c8895;
}

.assignment-manager__employee {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 14px;
    border-bottom: 1px solid #e3e8ee;
}

.assignment-manager__employee-details {
    display: flex;
    min-width: 0;
    flex-direction: column;
}

.assignment-manager__employee-actions {
    display: flex;
    flex: 0 0 auto;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 7px;
}

.assignment-manager__employee span {
    color: #7a8794;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
}

.assignment-manager__employee strong {
    color: #253545;
    font-size: 1.05rem;
}

.assignment-manager__employee small {
    margin-top: 2px;
    color: #788695;
}

.assignment-manager__hidden-alert {
    margin: 14px 0 0;
    padding: 9px 11px;
    font-size: 0.78rem;
}

.assignment-manager__section {
    margin-top: 18px;
}

.assignment-manager__section h3 {
    margin: 0 0 9px;
    color: #344556;
    font-size: 0.9rem;
}

.assignment-manager__assignments {
    display: flex;
    flex-direction: column;
    gap: 7px;
}

.assignment-manager__assignment {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 11px;
    border: 1px solid #dfe6ed;
    border-radius: 8px;
    background: #f9fbfd;
}

.assignment-manager__assignment-main {
    min-width: 0;
}

.assignment-manager__assignment-title {
    display: flex;
    align-items: center;
    gap: 7px;
    min-width: 0;
}

.assignment-manager__assignment-title i {
    flex: 0 0 16px;
    width: 16px;
    color: #6686a6;
    text-align: center;
}

.assignment-manager__assignment-title strong {
    overflow: hidden;
    color: #344556;
    font-size: 0.84rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.assignment-manager__assignment-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 5px;
    color: #71808f;
    font-size: 0.7rem;
}

.assignment-manager__assignment-actions {
    flex: 0 0 auto;
}

.assignment-manager__empty,
.assignment-manager__employee-empty {
    padding: 20px;
    border: 1px dashed #d5dde5;
    border-radius: 8px;
    color: #83909d;
    font-size: 0.8rem;
    text-align: center;
}

.assignment-manager__employee-empty {
    margin-top: 8px;
}

.assignment-manager__form {
    margin-top: 16px;
    padding: 13px;
    border: 1px solid #cddbeb;
    border-radius: 9px;
    background: #f4f8fc;
}

.assignment-manager__form--additional {
    border-color: #c9dedb;
    background: #f4faf9;
}

.assignment-manager__form-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
}

.assignment-manager__form-heading > div {
    display: flex;
    flex-direction: column;
}

.assignment-manager__form-heading strong {
    color: #2e465e;
    font-size: 0.9rem;
}

.assignment-manager__form-heading span {
    margin-top: 2px;
    color: #71808f;
    font-size: 0.73rem;
}

.assignment-manager__form-heading button {
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: #e5edf5;
    color: #60758a;
}

.assignment-manager__form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 7px;
}

@media (max-width: 575.98px) {
    .assignment-manager__employee-filter {
        flex-direction: column;
    }

    .assignment-manager__employee,
    .assignment-manager__assignment {
        align-items: stretch;
        flex-direction: column;
    }

    .assignment-manager__employee .btn,
    .assignment-manager__assignment-actions .btn {
        width: 100%;
    }

    .assignment-manager__employee-actions {
        width: 100%;
        flex-direction: column;
    }

    .assignment-manager__form-actions .btn {
        flex: 1 1 0;
    }
}
</style>

<style>
.assignment-manager-modal {
    z-index: 1090 !important;
}

body.assignment-manager-modal-open > .modal-backdrop {
    z-index: 1080 !important;
}
</style>

<style src="./workforce-viewport-modal.css"></style>
